import type { ForwardedRef} from "react";
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react"
import type { ReactChildren } from "../../types";
import type { ModalHandle } from "./Modal";
import { Modal } from "./Modal"
import { Info } from "../../icons"

export interface ConfirmProps {
    title?: string | ReactChildren,
    children?: ReactChildren
    valid?: () => boolean
    cancel?: () => void
    close?: () => void
    open?: boolean
    className?: string
    classNameContent?: string
    modal?: React.RefObject<ModalHandle>
    textConfirm?: string | ReactChildren
    classConfirm?: string
    textCancel?: string | ReactChildren
    overflow?: boolean
    /** icône du badge d'en-tête (défaut : info) */
    icon?: ReactChildren
}

export interface ConfirmHandle {
    close: () => void
    open: () => void
    toggle: () => void
}

export function ConfirmComponent({
    title,
    children,
    valid,
    cancel,
    close,
    open = false,
    className = "w-96 h-80",
    classNameContent = '',
    textConfirm = "Valider",
    classConfirm = "",
    textCancel = "Annuler",
    overflow = false,
    icon,
}: ConfirmProps,
ref: ForwardedRef<ConfirmHandle>): React.JSX.Element {

    const modal = useRef<ModalHandle>(null)

    const cancelHandle = useCallback((): void => {
        if(cancel) cancel()
        if(close) close()
        modal.current?.close()
    }, [modal.current, cancel])

    const validHandle = useCallback((): void => {
        let ok = true
        if(valid) ok = valid()
        if(ok) modal.current?.close()
    }, [modal.current, valid])

    useImperativeHandle(ref, () => ({
        close: () => {
            if(close) close()
            modal.current?.close()
        },
        open: () => {
            modal.current?.open()
        },
        toggle: () => {
            modal.current?.toggle()
        }
    }))

    const onClose = () => {
        if(close) close()
    }

    return (
        <Modal
            className={`${className} flex-col cursor-default`}
            open={open}
            ref={modal}
            onClose={onClose}
        >
            {/* Refonte « Aurora » : en-tête avec badge d'icône iris, contenu aéré, boutons en pilules */}
            { title ?
                <h2 className="shrink-0 px-6 pt-5 pb-4 flex items-center gap-3 font-semibold text-base leading-snug tracking-[-0.01em] text-ink dark:text-white">
                    <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-brand-50 to-fuchsia-50 dark:from-color7 dark:to-color7 text-brand-600 dark:text-white ring-1 ring-inset ring-brand-100 dark:ring-white/10">
                        { icon ?? <Info className="w-[18px] h-[18px] fill-current" /> }
                    </span>
                    <span className="flex-1 min-w-0">{ title }</span>
                </h2>
            : null }
            <div className={`flex-1 px-6 ${title ? 'pt-1' : 'pt-6'} pb-4 text-sm text-slate-600 dark:text-color3${ classNameContent ? ` ${classNameContent}`: '' }` + (overflow ? ' overflow-auto': '')}>
                { children }
            </div>
            <div className="shrink-0 px-6 py-4 gap-2 flex justify-end bg-slate-50/70 dark:bg-color7/50 border-t border-line/80 dark:border-color7">
                <button className="px-4 py-2 confirm-cancel-button" onClick={ cancelHandle } type="button">{textCancel}</button>
                <button className={ "px-5 py-2 " + ( classConfirm ? `confirm-button shadow-lift hover:brightness-110 ${classConfirm}` : 'confirm-valid-button' ) } onClick={ validHandle } type="button">{textConfirm}</button>
            </div>
        </Modal>
    )
}

const Confirm = forwardRef<ConfirmHandle, ConfirmProps>(ConfirmComponent)
Confirm.displayName = "Confirm"

export { Confirm }